import { SecurityType } from '@interfaces/models';
import { DecimalInputMoneyMarketTickersList } from '@interfaces/models/money-market-tickers-type';
import { toNumber } from '@sharedUtils';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import {
  InternalTransferMethodEnum,
  InternalTransferTypeEnum,
} from '@store/reducers/internal-transfer/internal-transfer.reducer';

import { isFixedIncome } from './fixed-income';
import { isOption, optionsContractMultiplier } from './margin-and-options.helpers';
import { math } from './mathjs/mathjs';

export { getEstimatedTotalTransferValue, getEstimatedValueOfTransferByShares } from '@sharedUtils';

/**
 * Checks if a position is a Vanguard Money Market fund
 */
export function isVanguardMoneyMarket(ticker: string): boolean {
  return DecimalInputMoneyMarketTickersList.includes(ticker);
}

/**
 * Checks if a position is a Vanguard Mutual Fund (V___X)
 */
export function isVanguardMutualFund(ticker: string): boolean {
  const vgMutualFundRegex = /^V[A-Z]{3}X$/;
  return vgMutualFundRegex.test(ticker) && !isVanguardMoneyMarket(ticker);
}

// VC pooled positions have no ticker; identity lives in the cusip's VCEP prefix.
export function isVanguardCharitablePool(cusip: string | null | undefined): boolean {
  return !!cusip?.startsWith('VCEP');
}

/**
 * Gets the appropriate decimal precision for a security type when transferring by percent
 */
export function getSharesDecimalPrecision(position: {
  ticker?: string | null;
  cusip?: string | null;
  settlementFund?: boolean;
}): number {
  const ticker = position.ticker ?? '';
  if (isVanguardCharitablePool(position.cusip)) return 3;
  // Vanguard Cash Deposit and Cash Plus bank sweep have no ticker
  // but are settlement fund
  if (position.settlementFund || isVanguardMoneyMarket(ticker)) return 2;
  if (isVanguardMutualFund(ticker)) return 3;
  return 4;
}

const mutualFundSecurityTypes: SecurityType[] = [SecurityType.MUTUAL_FUND, SecurityType.FUND];

/**
 * Decimal places a share quantity is shown to on the multi-IT review and confirm receipts.
 * Distinct from getSharesDecimalPrecision, which sets the precision percent-mode shares are
 * calculated to and the amounts page allocates and validates at.
 */
export function getSharesDisplayPrecision(position: {
  ticker?: string | null;
  cusip?: string | null;
  settlementFund?: boolean;
  securityType?: SecurityType;
}): number {
  const ticker = position.ticker ?? '';
  const securityType = position.securityType;

  if (isVanguardCharitablePool(position.cusip)) return 3;
  // Vanguard Cash Deposit and Cash Plus bank sweep have no ticker
  // but are settlement fund
  if (
    position.settlementFund ||
    isVanguardMoneyMarket(ticker) ||
    securityType === SecurityType.MONEY_MARKET_FUND
  ) {
    return 2;
  }
  // The ticker pattern only matches Vanguard funds, and securityType is absent on positions
  // rehydrated for an additional signer (getPositionPayload drops it), so check both.
  if (
    (securityType && mutualFundSecurityTypes.includes(securityType)) ||
    isVanguardMutualFund(ticker)
  ) {
    return 4;
  }
  return 3;
}

/**
 * Formats sharesToTransfer at its display precision, padded with trailing zeros so every
 * quantity of a given security type shows the same number of decimals.
 */
export function formatSharesToTransfer(position: {
  ticker?: string | null;
  cusip?: string | null;
  settlementFund?: boolean;
  securityType?: SecurityType;
  sharesToTransfer?: string | number | null;
}): string {
  return toNumber(position.sharesToTransfer).toFixed(getSharesDisplayPrecision(position));
}

export function getSharesToTransfer(
  percentage: number,
  position: CostBasisPosition,
  transferType: InternalTransferTypeEnum,
  transferMethod: InternalTransferMethodEnum,
): CostBasisPosition {
  const newPosition: CostBasisPosition = { ...position };
  if (transferType === InternalTransferTypeEnum.ENTIRE_ACCOUNT) {
    newPosition.sharesToTransfer = newPosition.shareQuantity;
  } else if (transferMethod === InternalTransferMethodEnum.PERCENT) {
    const precision = getSharesDecimalPrecision(position);
    newPosition.sharesToTransfer = Number(
      math.format(math.multiply(+newPosition.shareQuantity, math.divide(percentage, 100)), {
        notation: 'fixed',
        precision,
      }),
    );
  }
  return newPosition;
}
export function getEstimatedValueOfTransferByPercent(
  percent: number,
  position: CostBasisPosition,
  multiItMoToggleEnabled = false,
): CostBasisPosition {
  const newPosition: CostBasisPosition = { ...position };

  let value = 0;
  if (newPosition.settlementFund) {
    value = (toNumber(newPosition.balance) * percent) / 100;
  } else if (isFixedIncome(position.securityType)) {
    value = (toNumber(newPosition.sharesToTransfer) * toNumber(newPosition.price)) / 100;
  } else if (multiItMoToggleEnabled && isOption(position.securityType)) {
    value =
      toNumber(newPosition.sharesToTransfer) *
      toNumber(newPosition.price) *
      optionsContractMultiplier;
  } else {
    value = toNumber(newPosition.sharesToTransfer) * toNumber(newPosition.price);
  }

  newPosition.estValueOfTransfer = +math.format(value, { notation: 'fixed', precision: 2 });

  return newPosition;
}
